import classNames from 'classnames'
import { FormikErrors } from 'formik'
import { useState } from 'react'
import { removeElementAtIndex } from '../../helpers'
import { deepCopy } from '../../helpers/index'
import { useModalControl } from '../../hooks/useModalControl'
import {
  ComboItem,
  ComboItemParentRaw,
  CreateProductValues,
} from '../../redux-toolkits/products/products.type'
import { Button } from '../button'
import { ActionChip } from '../cards/actionChip'
import Icon from '../icon'
import { Checkbox } from '../inputs/Checkbox'
import { AddComboItemModal } from '../modal/app_modals/addComboItemModal'
import { AddComboSectionModal } from '../modal/app_modals/addComboSectionodal'
import { HeaderSubheader } from '../typography/HeaderSubheader'

export const ManageProductCombos = ({
  values,
  setFieldValue,
}: {
  values: CreateProductValues
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean,
  ) => Promise<void> | Promise<FormikErrors<CreateProductValues>>
}) => {
  const [selectedComboItem, setSelectedComboItem] = useState<{
    index?: number
    item?: ComboItem
    parent?: ComboItemParentRaw
    parentIdx?: number
  }>()

  const { handleModalOpen, setModalOpen, modal, modalOpen, handleModalClose } =
    useModalControl()

  return (
    <div className="flex flex-col gap-6 pb-10 border-b border-divider-gray">
      <div className="flex flex-wrap gap-4 items-start justify-between">
        <HeaderSubheader
          title="Product Combo"
          text="Specify combo products"
          className="w-full md:w-fit"
        />

        {values.isCombo ? (
          <Button
            type="button"
            label="Add combo item"
            onClick={() => {
              handleModalOpen('manageComboSectionModal')
            }}
            className="!w-fit !px-0 !py-0 !gap-2"
            variant="plain"
            icon="plus-circle"
            iconPosition="right"
            iconSize={20}
          />
        ) : undefined}
      </div>

      <Checkbox
        checkStatus={values.isCombo}
        text="This a combo product"
        handleChange={(status) => {
          setFieldValue('isCombo', status)
        }}
      />

      {values.isCombo && (
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-3 gap-6 items-center">
          {values?.comboItems?.map((parentItem, parentIdx) => {
            const key = `comboItemParent-${parentIdx}`

            return (
              <div
                key={key}
                className="h-full relative flex flex-col gap-4 text-base text-black border-[2px] rounded-2xl p-4 border-default-gray"
              >
                <div className="w-full flex items-center gap-3">
                  <p
                    className={classNames(
                      'text-primary text-base flex-1 text-left',
                      {
                        'opacity-0': !parentItem.required,
                      },
                    )}
                  >
                    *
                  </p>

                  <button
                    type="button"
                    className="w-fit"
                    onClick={() => {
                      setSelectedComboItem({
                        index: undefined,
                        item: undefined,
                        parent: parentItem,
                        parentIdx: parentIdx,
                      })
                      handleModalOpen('manageComboSectionModal')
                    }}
                  >
                    <Icon id="edit" className="" width={16} height={16} />
                  </button>

                  <button
                    type="button"
                    className="w-fit"
                    onClick={() => {
                      const combosCopy = deepCopy(values.comboItems ?? [])
                      setFieldValue(
                        'comboItems',
                        removeElementAtIndex(combosCopy, parentIdx),
                      )
                    }}
                  >
                    <Icon
                      id="bin"
                      className="text-danger"
                      width={16}
                      height={16}
                    />
                  </button>
                </div>

                <div className="flex flex-col gap-2">
                  <p>Name</p>
                  <p className="text-sec-black">{parentItem.groupName}</p>
                </div>

                <div className="flex flex-col gap-2">
                  <p>Description</p>
                  <p className="text-sec-black">
                    {parentItem.groupDescription}
                  </p>
                </div>

                <div className="flex items-center gap-4 flex-wrap">
                  {(parentItem.items ?? []).map((comboItem, comboItemIdx) => {
                    const childkey = `comboItem-${parentIdx}-${comboItemIdx}`
                    return (
                      <ActionChip
                        key={childkey}
                        name={comboItem.name}
                        onClick={() => {
                          setSelectedComboItem({
                            index: comboItemIdx,
                            item: comboItem,
                            parent: parentItem,
                            parentIdx: parentIdx,
                          })
                          handleModalOpen('manageComboItemModal')
                        }}
                        onDismiss={() => {
                          const comboList = deepCopy(values.comboItems ?? [])

                          comboList[parentIdx] = {
                            ...comboList[parentIdx],
                            items: removeElementAtIndex(
                              comboList[parentIdx].items,
                              comboItemIdx,
                            ),
                          }
                          setFieldValue('comboItems', comboList)
                        }}
                      />
                    )
                  })}
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setSelectedComboItem({
                      index: undefined,
                      item: undefined,
                      parent: parentItem,
                      parentIdx: parentIdx,
                    })
                    handleModalOpen('manageComboItemModal')
                  }}
                  className="w-fit flex items-center gap-2 mt-auto"
                >
                  Add combo item
                  <Icon id="plus-circle" width={20} height={20} />
                </button>
              </div>
            )
          })}
        </div>
      )}

      <AddComboSectionModal
        open={modalOpen && modal === 'manageComboSectionModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
          setSelectedComboItem(undefined)
        }}
        onSuccessCallback={(item) => {
          const combos = deepCopy(values.comboItems ?? [])
          if (selectedComboItem && selectedComboItem.parentIdx !== undefined) {
            combos[selectedComboItem.parentIdx] = item
          } else {
            combos.push(item)
          }
          setFieldValue('comboItems', combos)
          setSelectedComboItem(undefined)
          handleModalClose()
        }}
        comboParent={selectedComboItem?.parent}
        mode={selectedComboItem ? 'edit' : 'create'}
      />

      <AddComboItemModal
        open={modalOpen && modal === 'manageComboItemModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
          setSelectedComboItem(undefined)
        }}
        onSuccessCallback={(item) => {
          const combos = deepCopy(values.comboItems ?? [])
          if (
            selectedComboItem &&
            selectedComboItem.parentIdx !== undefined &&
            selectedComboItem.index !== undefined
          ) {
            combos[selectedComboItem.parentIdx].items[selectedComboItem.index] =
              item
          } else if (
            selectedComboItem &&
            selectedComboItem.parentIdx !== undefined &&
            selectedComboItem.index === undefined
          ) {
            combos[selectedComboItem.parentIdx].items.push(item)
          }
          setFieldValue('comboItems', combos)
          setSelectedComboItem(undefined)
          handleModalClose()
        }}
        comboItem={selectedComboItem?.item}
        mode={selectedComboItem?.item ? 'edit' : 'create'}
      />
    </div>
  )
}
